import type {
  GetPickingResponse,
  ScanRequest,
  ScanResponse,
  LookupRequest,
  LookupResponse,
  ValidatePickingRequest,
  ValidatePickingResponse,
  StartTransferRequest,
  StartTransferResponse,
  SetTransferDestinationRequest,
  CommitTransferResponse,
  ScannedBagInput,
  ManualScanRequest,
  ManualScanResponse,
  ManualLookupRequest,
  PrintersResponse,
  PrintLabelRequest,
  PrintLabelResponse,
} from "@pm-barcode/shared";

export interface ClientConfig {
  apiBase: string;
  deviceToken: string;
  deviceId: string;
}

export class RequestError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "RequestError";
  }
}

async function send<T>(
  config: ClientConfig,
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${config.apiBase}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      "X-Device-Token": config.deviceToken,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const json = (await res.json()) as Record<string, unknown>;
  if (!res.ok) {
    throw new RequestError(
      (json["code"] as string | undefined) ?? "unknown_error",
      (json["message"] as string | undefined) ?? res.statusText,
      res.status,
    );
  }
  return json as T;
}

export interface PickingSummary {
  name: string;
  state: string;
  scheduledDate: string | null;
  origin: string | null;
  partner: string | null;
}

export interface LocationSummary {
  id: number;
  name: string;
  barcode: string;
}

/** Which company this device scans for — returned by GET /session. */
export interface SessionInfo {
  company: string;
  displayName: string;
  accent: string;
  warehouse: string;
}

export function createApiClient(config: ClientConfig) {
  return {
    getSession(): Promise<SessionInfo> {
      return send<SessionInfo>(config, "GET", "/session");
    },
    listPickings(type: "receipt" | "dispatch"): Promise<{ pickings: PickingSummary[] }> {
      return send<{ pickings: PickingSummary[] }>(config, "GET", `/picking?type=${type}`);
    },
    listLocations(): Promise<{ locations: LocationSummary[] }> {
      return send<{ locations: LocationSummary[] }>(config, "GET", "/transfer/locations");
    },
    getPicking(ref: string): Promise<GetPickingResponse> {
      return send<GetPickingResponse>(config, "GET", `/picking/${encodeURIComponent(ref)}`);
    },
    postScan(body: ScanRequest): Promise<ScanResponse> {
      return send<ScanResponse>(config, "POST", "/scan", body);
    },
    scanManual(body: ManualScanRequest): Promise<ManualScanResponse> {
      return send<ManualScanResponse>(config, "POST", "/scan/manual", body);
    },
    postManualLookup(body: ManualLookupRequest): Promise<LookupResponse> {
      return send<LookupResponse>(config, "POST", "/lookup/manual", body);
    },
    listPrinters(): Promise<PrintersResponse> {
      return send<PrintersResponse>(config, "GET", "/print/printers");
    },
    printLabel(body: PrintLabelRequest): Promise<PrintLabelResponse> {
      return send<PrintLabelResponse>(config, "POST", "/print/label", body);
    },
    postLookup(body: LookupRequest): Promise<LookupResponse> {
      return send<LookupResponse>(config, "POST", "/lookup", body);
    },
    validatePicking(ref: string, body: ValidatePickingRequest): Promise<ValidatePickingResponse> {
      return send<ValidatePickingResponse>(
        config,
        "POST",
        `/picking/${encodeURIComponent(ref)}/validate`,
        body,
      );
    },
    startTransfer(body: StartTransferRequest): Promise<StartTransferResponse> {
      return send<StartTransferResponse>(config, "POST", "/transfer/start", body);
    },
    setTransferDestination(transferId: number, body: SetTransferDestinationRequest): Promise<void> {
      return send<void>(config, "POST", `/transfer/${transferId}/destination`, body);
    },
    commitTransfer(transferId: number, scans: ScannedBagInput[]): Promise<CommitTransferResponse> {
      return send<CommitTransferResponse>(config, "POST", `/transfer/${transferId}/commit`, { scans });
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

export interface AuditEntry {
  id: string;
  deviceId: string;
  rawBarcode: string;
  ruleId: string | null;
  outcome: string;
  normalised: Record<string, unknown> | null;
  odooTxRef: string | null;
  exception: string | null;
  createdAt: string;
  company: string | null;
}

export async function fetchAuditLog(
  apiBase: string,
  adminToken: string,
  pickingRef: string,
): Promise<AuditEntry[]> {
  const res = await fetch(`${apiBase}/audit/${encodeURIComponent(pickingRef)}`, {
    headers: { "X-Admin-Token": adminToken },
  });
  const json = (await res.json()) as unknown;
  if (!res.ok) {
    const err = json as Record<string, unknown>;
    throw new RequestError(
      (err["code"] as string | undefined) ?? "unknown_error",
      (err["message"] as string | undefined) ?? res.statusText,
      res.status,
    );
  }
  return json as AuditEntry[];
}
